import { IGetAppointmentOccurrenceByUser, IOccurrenceDetail } from "../../models/dto/agendaApi/IGetAppointmentsByUser";
import { AppointmentTypeEnum } from "../../models/enum/AgendaApi/AppointmentTypeEnum";

export interface ICalendarEvent {
  id: number;
  title: string;
  start: Date;
  end: Date;
  occurrenceID: number | null;
  resource: IGetAppointmentOccurrenceByUser;
}

export type DeleteOccurrenceMode = "onlyThisDate" | "byRange" | "selectedDates";

export interface IDeleteSeriesPayload {
  appointmentID: number;
}

export interface IDeleteOccurrencePayload {
  occurrenceIDs: number[];   
  appointmentID: number
}

export interface ISelectableOccurrence {
  occurrenceID: number | null; 
  dateStr: string;             
}

export const isPastDate = (date: Date, now: Date = new Date()): boolean => {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const d     = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return d < today;
};

export const isOccurrenceInPast = (
  date: Date,
  hourOut: string | null | undefined,
  now: Date = new Date()
): boolean => {
  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  if (d < todayMidnight) return true;
  if (d > todayMidnight) return false;
  // Same day: check if end time has already passed
  if (!hourOut) return false;
  const [h = 0, m = 0, s = 0] = hourOut.split(":").map(Number);
  const endTime = new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, m, s);
  return endTime.getTime() <= now.getTime();
};

export const isAgendaOccurrenceDate = (
  date: Date,
  agenda: IGetAppointmentOccurrenceByUser
): boolean => {
  const dateStr = toDateStr(date);

  if (agenda.occurrences && agenda.occurrences.length > 0) {
    return agenda.occurrences.some(
      (occ) => !!occ.occurrenceDate && occ.occurrenceDate.split("T")[0] === dateStr
    );
  }

  if (!agenda.dateStart) return false;
  const rangeStart = parseLocalDate(agenda.dateStart);
  const rangeEnd   = parseLocalDate(agenda.dateEnd || agenda.dateStart);
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  if (d < rangeStart || d > rangeEnd) return false;
  const dayFlags = [
    agenda.sunday, agenda.monday, agenda.tuesday, agenda.wednesday,
    agenda.thursday, agenda.friday, agenda.saturday,
  ];
  return !!dayFlags[date.getDay()];
};

export const isSelectableDate = (
  date: Date,
  agenda: IGetAppointmentOccurrenceByUser,
  now: Date = new Date()
): boolean => isAgendaOccurrenceDate(date, agenda) && !isOccurrenceInPast(date, agenda.assignHourOut, now);


export const getOccurrenceForDate = (
  date: Date,
  agenda: IGetAppointmentOccurrenceByUser
): IOccurrenceDetail | null => {
  const dateStr = toDateStr(date);
  if (!agenda.occurrences || agenda.occurrences.length === 0) return null;
  return (
    agenda.occurrences.find((occ) => occ.occurrenceDate?.split("T")[0] === dateStr) ?? null
  );
};

export const buildSelectableOccurrences = (
  agenda: IGetAppointmentOccurrenceByUser,
  now: Date = new Date()
): ISelectableOccurrence[] => {
  const result: ISelectableOccurrence[] = [];

  if (agenda.occurrences && agenda.occurrences.length > 0) {
    agenda.occurrences.forEach((occ) => {
      if (!occ.occurrenceDate) return;
      const date = parseLocalDate(occ.occurrenceDate);
      const effectiveHourOut = occ.assignHourOut ?? agenda.assignHourOut;
      if (!isOccurrenceInPast(date, effectiveHourOut, now)) {
        result.push({
          occurrenceID: occ.appointmentOccurrenceID,
          dateStr: occ.occurrenceDate.split("T")[0],
        });
      }
    });
  } else {
    if (!agenda.dateStart) return result;
    const rangeStart = parseLocalDate(agenda.dateStart);
    const rangeEnd   = parseLocalDate(agenda.dateEnd || agenda.dateStart);
    const cursor     = new Date(rangeStart);
    const dayFlags   = [
      agenda.sunday, agenda.monday, agenda.tuesday, agenda.wednesday,
      agenda.thursday, agenda.friday, agenda.saturday,
    ];
    let remaining = 366; // guard against unbounded scans
    while (cursor <= rangeEnd && remaining > 0) {
      if (dayFlags[cursor.getDay()] && !isOccurrenceInPast(new Date(cursor), agenda.assignHourOut, now)) {
        result.push({ occurrenceID: null, dateStr: toDateStr(cursor) });
      }
      cursor.setDate(cursor.getDate() + 1);
      remaining--;
    }
  }

  result.sort((a, b) => a.dateStr.localeCompare(b.dateStr));
  return result;
};

const toDateStr = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const RECURRENT_TYPE_IDS = [AppointmentTypeEnum.Permanent, AppointmentTypeEnum.Route];

export const isRecurrentAppointment = (typeID: number): boolean =>
  RECURRENT_TYPE_IDS.includes(typeID);

const toDate = (value: Date | string): Date => new Date(value);

export const isPastOccurrence = (
  startDateTime: Date | string,
  endDateTime?: Date | string,
  now: Date = new Date()
): boolean => {
  const compareDate = endDateTime ? toDate(endDateTime) : toDate(startDateTime);
  return compareDate.getTime() < now.getTime();
};


export const APPOINTMENT_TYPE_COLORS: Readonly<Record<number, string>> = {
  [AppointmentTypeEnum.Permanent]: '#A3C4F3',
  [AppointmentTypeEnum.Route]:     '#B9FBC0',
  [AppointmentTypeEnum.Visit]:     '#CFBAF0',
  [AppointmentTypeEnum.VisitAlt]:  '#FFCFD2',
};

const APPOINTMENT_TYPE_COLOR_FALLBACK = '#B8CFF5';

export const getAppointmentTypeColor = (typeID: number): string =>
  APPOINTMENT_TYPE_COLORS[typeID] ?? APPOINTMENT_TYPE_COLOR_FALLBACK;

type RecurringDayKey = keyof Pick<
  IGetAppointmentOccurrenceByUser,
  "monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday"
>;

const DAY_FLAGS: { key: RecurringDayKey; dayIndex: number }[] = [
  { key: "sunday",    dayIndex: 0 },
  { key: "monday",    dayIndex: 1 },
  { key: "tuesday",   dayIndex: 2 },
  { key: "wednesday", dayIndex: 3 },
  { key: "thursday",  dayIndex: 4 },
  { key: "friday",    dayIndex: 5 },
  { key: "saturday",  dayIndex: 6 },
];

const parseTime = (baseDate: Date, timeStr: string): Date => {
  const parts = (timeStr || "0:00").split(":");
  const hours = parseInt(parts[0], 10) || 0;
  const minutes = parseInt(parts[1], 10) || 0;
  const result = new Date(baseDate);
  result.setHours(hours, minutes, 0, 0);
  return result;
};

export const buildOccurrenceDateTime = (
  occurrenceDate: Date | string,
  timeStr: string
): Date => {
  const baseDate =
    occurrenceDate instanceof Date
      ? new Date(occurrenceDate)
      : parseLocalDate(String(occurrenceDate));

  return parseTime(baseDate, timeStr);
};

type AppointmentOccurrenceInput = Pick<
  IGetAppointmentOccurrenceByUser,
  "dateStart" | "assignHourIn" | "assignHourOut"
>;

export const isPastAppointmentOccurrence = (
  appointment: AppointmentOccurrenceInput,
  occurrenceDate?: Date | string,
  now: Date = new Date()
): boolean => {
  const baseDate = occurrenceDate ?? appointment.dateStart;
  const startDateTime = buildOccurrenceDateTime(baseDate, appointment.assignHourIn);
  const endDateTime = buildOccurrenceDateTime(
    baseDate,
    appointment.assignHourOut || appointment.assignHourIn
  );

  return isPastOccurrence(startDateTime, endDateTime, now);
};
export const parseLocalDate = (dateStr: string): Date => {
  const dateOnly = dateStr.split("T")[0];
  const [year, month, day] = dateOnly.split("-").map(Number);
  return new Date(year, month - 1, day);
};


export const mapAppointmentsToCalendarEvents = (
  appointments: IGetAppointmentOccurrenceByUser[]
): ICalendarEvent[] => {
  const events: ICalendarEvent[] = [];

  appointments.forEach((appt) => {
    if (appt.occurrences && appt.occurrences.length > 0) {
      appt.occurrences.forEach((occ) => {
        if (!occ.occurrenceDate) return;

        const hourIn  = occ.assignHourIn  || appt.assignHourIn;
        const hourOut = occ.assignHourOut || appt.assignHourOut;
        const title = occ.isException && occ.pointOfSale ? occ.pointOfSale : appt.pointOfSale;

        events.push({
          id: appt.appointmentID,
          title,
          start: buildOccurrenceDateTime(occ.occurrenceDate, hourIn),
          end:   buildOccurrenceDateTime(occ.occurrenceDate, hourOut),
          occurrenceID: occ.appointmentOccurrenceID,
          resource: appt,
        });
      });
    } else {
      if (!appt.dateStart || !appt.dateEnd) return;

      const rangeStart = parseLocalDate(appt.dateStart);
      const rangeEnd   = parseLocalDate(appt.dateEnd);
      const cursor     = new Date(rangeStart);

      while (cursor <= rangeEnd) {
        const flag = DAY_FLAGS.find((f) => f.dayIndex === cursor.getDay());

        if (flag && appt[flag.key]) {
          events.push({
            id: appt.appointmentID,
            title: appt.pointOfSale,
            start: buildOccurrenceDateTime(new Date(cursor), appt.assignHourIn),
            end:   buildOccurrenceDateTime(new Date(cursor), appt.assignHourOut || appt.assignHourIn),
            occurrenceID: null,
            resource: appt,
          });
        }

        cursor.setDate(cursor.getDate() + 1);
      }
    }
  });

  return events;
};
